export type FitTier = "Strong" | "Moderate" | "At Risk";
export type PodSize = "Solo" | "Pair" | "Trio" | "Quad";
export type AlertPreset = "all" | "atRisk" | "unassigned" | "partial" | "strong";

export interface SolutionFilterState {
  preset: AlertPreset;
  status: string[];
  fit: FitTier[];
  pod: PodSize[];
  intensityMin: number;
  intensityMax: number;
  tier: string[];
  expectation: number[];
  constraints: string[];
  resource: string[];
}

export interface FilterableSolution {
  status: string;
  intensity: number;
  podSize: PodSize;
  account: { tier: string; expectationLevel: number };
  assignments: Array<{ role: string; resource: { id: string } }>;
  constraints: Array<{ id: string }>;
  fit: { tier: FitTier } | null;
}

export const INTENSITY_MIN = 1;
export const INTENSITY_MAX = 8;
export const FIT_TIERS: FitTier[] = ["Strong", "Moderate", "At Risk"];
export const POD_SIZES: PodSize[] = ["Solo", "Pair", "Trio", "Quad"];
export const SOLUTION_STATUSES = ["Active", "Pending", "Closed"] as const;

export const DEFAULT_FILTERS: SolutionFilterState = {
  preset: "all",
  status: [],
  fit: [],
  pod: [],
  intensityMin: INTENSITY_MIN,
  intensityMax: INTENSITY_MAX,
  tier: [],
  expectation: [],
  constraints: [],
  resource: [],
};

function requiredRoles(pod: PodSize): string[] {
  if (pod === "Solo") return ["R1"];
  if (pod === "Pair") return ["R1", "R2"];
  if (pod === "Quad") return ["R1", "R2", "R3", "R4"];
  return ["R1", "R2", "R3"];
}

function matchesPreset(s: FilterableSolution, preset: AlertPreset): boolean {
  if (preset === "all") return true;
  const present = new Set(s.assignments.map((a) => a.role));
  const hasR1 = present.has("R1");
  const full = requiredRoles(s.podSize).every((r) => present.has(r));
  if (preset === "unassigned") return !hasR1;
  if (preset === "partial") return hasR1 && !full;
  if (preset === "strong") return full && s.fit?.tier === "Strong";
  return !hasR1 || s.fit?.tier === "At Risk"; // atRisk
}

export function matchesFilters(s: FilterableSolution, state: SolutionFilterState): boolean {
  if (!matchesPreset(s, state.preset)) return false;
  if (state.status.length > 0 && !state.status.includes(s.status)) return false;
  if (state.fit.length > 0 && (!s.fit || !state.fit.includes(s.fit.tier))) return false;
  if (state.pod.length > 0 && !state.pod.includes(s.podSize)) return false;
  if (state.tier.length > 0 && !state.tier.includes(s.account.tier)) return false;
  if (state.expectation.length > 0 && !state.expectation.includes(s.account.expectationLevel))
    return false;
  if (s.intensity < state.intensityMin || s.intensity > state.intensityMax) return false;
  if (state.constraints.length > 0) {
    const have = new Set(s.constraints.map((c) => c.id));
    if (!state.constraints.every((id) => have.has(id))) return false;
  }
  if (state.resource.length > 0) {
    const have = new Set(s.assignments.map((a) => a.resource.id));
    if (!state.resource.some((id) => have.has(id))) return false;
  }
  return true;
}

export function activeFilterCount(state: SolutionFilterState): number {
  let n = 0;
  if (state.preset !== "all") n++;
  if (state.status.length) n++;
  if (state.fit.length) n++;
  if (state.pod.length) n++;
  if (state.tier.length) n++;
  if (state.expectation.length) n++;
  if (state.constraints.length) n++;
  if (state.resource.length) n++;
  if (state.intensityMin !== INTENSITY_MIN || state.intensityMax !== INTENSITY_MAX) n++;
  return n;
}

function csv(v: string[]): string {
  return v.join(",");
}
function splitCsv(v: unknown): string[] {
  return typeof v === "string" && v.length > 0 ? v.split(",").filter(Boolean) : [];
}

export function encodeFilters(state: SolutionFilterState): Record<string, string> {
  const out: Record<string, string> = {};
  if (state.preset !== "all") out.preset = state.preset;
  if (state.status.length) out.status = csv(state.status);
  if (state.fit.length) out.fit = csv(state.fit);
  if (state.pod.length) out.pod = csv(state.pod);
  if (state.tier.length) out.tier = csv(state.tier);
  if (state.expectation.length) out.expectation = csv(state.expectation.map(String));
  if (state.constraints.length) out.constraints = csv(state.constraints);
  if (state.resource.length) out.resource = csv(state.resource);
  if (state.intensityMin !== INTENSITY_MIN || state.intensityMax !== INTENSITY_MAX)
    out.intensity = `${state.intensityMin}-${state.intensityMax}`;
  return out;
}

function decodePreset(v: unknown): AlertPreset {
  return v === "atRisk" || v === "unassigned" || v === "partial" || v === "strong" ? v : "all";
}
function clampLevel(n: number): number {
  return n < INTENSITY_MIN ? INTENSITY_MIN : n > INTENSITY_MAX ? INTENSITY_MAX : n;
}

export function decodeFilters(search: Record<string, unknown>): SolutionFilterState {
  let intensityMin = INTENSITY_MIN;
  let intensityMax = INTENSITY_MAX;
  if (typeof search.intensity === "string") {
    const parts = search.intensity.split("-");
    const lo = Number(parts[0]);
    const hi = Number(parts[1]);
    if (Number.isInteger(lo) && Number.isInteger(hi) && lo <= hi) {
      intensityMin = clampLevel(lo);
      intensityMax = clampLevel(hi);
    }
  }
  const fit = splitCsv(search.fit).filter((v): v is FitTier => FIT_TIERS.includes(v as FitTier));
  const pod = splitCsv(search.pod).filter((v): v is PodSize => POD_SIZES.includes(v as PodSize));
  const expectation = splitCsv(search.expectation)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 5);
  return {
    preset: decodePreset(search.preset),
    status: splitCsv(search.status),
    fit,
    pod,
    intensityMin,
    intensityMax,
    tier: splitCsv(search.tier),
    expectation,
    constraints: splitCsv(search.constraints),
    resource: splitCsv(search.resource),
  };
}
